import * as CL from '@design-system/component-library';
import { Controller, useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import {
  dateMustBeGreaterThanTodayAndOutsideWeekendsMsg,
  selectEffectiveDateMsg,
  t,
  willTheChangeTakeEffectImmediatelyMsg,
} from '../../../common/i18n/index.js';
import { DatePicker } from '../../../common/react-hook-form/components/DatePicker.js';
import { isWeekend } from '../../../common/utils/dateUtils.js';
import { ToggleWithSubComponent } from '../../ToggleWithSubComponent/ToggleWithSubComponent.js';
import type { FormEditProps } from './EOperatorChangeRequestForm.js';

const FIELD_NAME = 'effectiveDate';

const validateDate = (value: string) => {
  const [day, month, year] = value.split('.');
  const givenDate = new Date(`${year}-${month}-${day}`);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  givenDate.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);

  return givenDate < tomorrow || isWeekend(givenDate)
    ? t.H5I1(dateMustBeGreaterThanTodayAndOutsideWeekendsMsg)
    : undefined;
};

const FormEditField = () => (
  <DatePicker
    disableAllPastDays={true}
    disableWeekends={true}
    blockedDates={[new Date()]}
    label={t.NDRY(selectEffectiveDateMsg)}
    name={FIELD_NAME}
    required={true}
    validate={validateDate}
  />
);

const FormInfoField = () => {
  const { getValues } = useFormContext();
  return (
    <div className={dsClass.INPUT}>
      <div className={dsClass.INPUT_LABELAREA}>
        <label className={dsClass.INPUT_LABELAREA_LABEL}>{t.NDRY(selectEffectiveDateMsg)}</label>
      </div>
      <div className={dsClass.INPUT_INPUTAREA}>
        <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}>
          <CL.Icon icon="calendar" />
          <span className={dsClass.PADDING_LEFT_2}>{getValues(FIELD_NAME)}</span>
        </div>
      </div>
    </div>
  );
};

export const WillChangeTakeEffectImmediately = ({ isEditing = true }: FormEditProps) => {
  const { setValue, clearErrors } = useFormContext();
  const handleToggle = (value: boolean) => {
    if (value) {
      setValue(FIELD_NAME, '');
      clearErrors(FIELD_NAME);
    }
  };
  return (
    <Controller
      name="willChangeTakeEffectImmediately"
      render={({ field: { onChange } }) => (
        <ToggleWithSubComponent
          name="willChangeTakeEffectImmediately"
          title={t.CACT(willTheChangeTakeEffectImmediatelyMsg)}
          infoText={t.VF8X(
            'Yes: the new information will take effect from the first possible invoice. Please note that the change will not affect any invoices that have already been created.\n\n' +
              'No: you can provide your updated invoice details in advance, and the change will take effect on the date you choose.'
          )}
          onToggle={value => {
            onChange(value);
            handleToggle(value);
          }}
          subComponent={isEditing ? <FormEditField /> : <FormInfoField />}
          showSubComponentWhenToggled={false}
          initialValue={true}
          disabled={!isEditing}
        />
      )}
    />
  );
};
